// §9.5 — the scoped filesystem an app actually sees.
//
// `HangarAPI.fs` is this and nothing else. Every method takes the calling instance, runs the
// path through the **single** confinement function (§7.1), and only then reaches the tree. There
// is no unscoped path from an app to the VFS, and no second copy of the permission logic.
//
// Grants are per **window instance**, not per app: §9.5 says a picker grant is "per session, per
// path, never inherited by a new window", so a second window of the same app starts with none.
import {
  HangarApiError,
  ROOT_APPS,
  basename,
  joinVfs,
  normalizeVfsPath,
  userRoot,
  type AppInstanceContext,
  type FileEntry,
  type FsEvent,
  type PickOpts,
  type VersionEntry,
  type VfsPath,
} from '@hangar/shared';
import { confine, type ConfineError, type VfsCaller } from './confine';
import { OpfsError } from './opfs';
import { VfsError, type VfsTree } from './tree';
import type { VersionStore } from './versions';
import type { TrashService } from './trash';

export interface VfsGateway {
  list(ctx: AppInstanceContext, dir: VfsPath): Promise<FileEntry[]>;
  read(ctx: AppInstanceContext, path: VfsPath): Promise<Uint8Array>;
  readText(ctx: AppInstanceContext, path: VfsPath): Promise<string>;
  write(ctx: AppInstanceContext, path: VfsPath, data: Uint8Array | string): Promise<FileEntry>;
  mkdir(ctx: AppInstanceContext, path: VfsPath): Promise<FileEntry>;
  move(ctx: AppInstanceContext, from: VfsPath, to: VfsPath): Promise<FileEntry>;
  /** §7.8 — to the Trash, never an unlink. */
  remove(ctx: AppInstanceContext, path: VfsPath): Promise<void>;
  versions(ctx: AppInstanceContext, path: VfsPath): Promise<VersionEntry[]>;
  restore(ctx: AppInstanceContext, path: VfsPath, versionId: string): Promise<FileEntry>;
  watch(ctx: AppInstanceContext, dir: VfsPath, cb: (event: FsEvent) => void): Promise<() => void>;
  /** §7.7 / §9.5 — the shell-drawn picker. The gesture is the grant. */
  pickOpen(ctx: AppInstanceContext, opts?: PickOpts): Promise<FileEntry[]>;
  pickSave(ctx: AppInstanceContext, opts?: PickOpts): Promise<FileEntry>;

  /** §9.5 — what a shell-drawn picker hands back. The gesture is the grant. */
  grant(instanceKey: string, paths: readonly VfsPath[]): void;
  /** Called when a window closes; a grant never outlives the window that earned it. */
  revoke(instanceKey: string): void;
  /** For App Inspector (§11.12) and the tests. */
  grantsFor(instanceKey: string): VfsPath[];
  /** The caller record a given instance resolves to — exported so §11.12 can show it. */
  callerFor(ctx: AppInstanceContext): VfsCaller;
  /**
   * Could this caller reach `path`? Asked before "Open with" hands a path to another window
   * (§9.2), so an app cannot make the shell surface a file the app itself may not read.
   */
  canReach(ctx: AppInstanceContext, path: VfsPath, access: 'read' | 'write'): boolean;
}

export interface VfsGatewayOptions {
  tree: VfsTree;
  versions: VersionStore;
  trash: TrashService;
  /**
   * §9.5 — apps that are *the* file manager: all of user space and `/Apps`, `/System` still
   * read-only. §11.1 File Explorer and §11.11 Recycle Bin are the two that qualify.
   */
  fileManagers?: readonly string[];
  /** §9.5's "the paths its spec names in §11", per app id. */
  extraRoots?: (appId: string) => readonly VfsPath[];
  /**
   * §18 F4 — a write that failed for want of room. The app gets `QUOTA_EXCEEDED` either way;
   * this is how the *shell* hears about it, so the user is offered the two ways out rather than
   * left with whatever the app decided to say.
   */
  onQuotaExceeded?: (path: string) => void;
  /** §19.4 — the shell-drawn picker. Injected, because the kernel's UI lives above this. */
  pickFiles?: (
    request: {
      mode: 'open' | 'save';
      title: string;
      startIn: VfsPath;
      suggestedName?: string;
      extensions: string[];
      multiple: boolean;
      directory: boolean;
    },
    appId: string,
  ) => Promise<VfsPath[] | null>;
}

/**
 * §9.5 — the built-ins whose reach is "all of user space and `/Apps`" rather than their own
 * `/Apps/<id>/`. §9.5 gives the File Explorer that outright and grants every other built-in "the
 * paths its spec names in §11"; these are the three whose §11 entry names the whole tree.
 *
 * `terminal` is here because §11.2 defines it as **"A shell over the VFS"** — a shell that could
 * not `ls` the user's Documents is not one, and every alternative was worse: a picker before each
 * command is not a shell, and a per-command grant is the same reach with more moving parts
 * (D-165). It is a deliberate, listed exception, which is the point of the list existing.
 */
const DEFAULT_FILE_MANAGERS = ['explorer', 'recycle-bin', 'terminal'] as const;

/** §7.1/§9.5 refusals, and the store's own errors, as the codes §4.1 defines. */
export function toApiError(
  error: unknown,
  path: string,
  onQuota?: (path: string) => void,
): HangarApiError {
  if (error instanceof HangarApiError) return error;

  const code =
    error instanceof OpfsError || error instanceof VfsError
      ? (error.code as ConfineError | string)
      : null;

  switch (code) {
    case 'FORBIDDEN':
    case 'READ_ONLY':
    case 'REFUSED':
      return new HangarApiError('FORBIDDEN', messageOf(error), { path, reason: code });
    case 'NOT_FOUND':
      return new HangarApiError('NOT_FOUND', messageOf(error), { path });
    case 'QUOTA':
      // §18 F4 — "never a silent failure". The app gets the error *and* the shell is told, so
      // the user sees the two ways out rather than only the app's own message.
      onQuota?.(path);
      return new HangarApiError('QUOTA_EXCEEDED', messageOf(error), { path });
    case 'UNSUPPORTED':
      return new HangarApiError('UNSUPPORTED', messageOf(error), { path });
    case null:
      return new HangarApiError('INTERNAL', messageOf(error), { path });
    default:
      // Everything else out of §7.1's grammar — a relative segment, a NUL, an over-long name.
      return new HangarApiError('BAD_REQUEST', messageOf(error), { path, reason: code });
  }
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function createVfsGateway(options: VfsGatewayOptions): VfsGateway {
  const { tree, versions, trash } = options;
  const fileManagers = new Set(options.fileManagers ?? DEFAULT_FILE_MANAGERS);
  const grants = new Map<string, Set<VfsPath>>();

  function addGrants(instanceKey: string, paths: readonly VfsPath[]): void {
    const set = grants.get(instanceKey) ?? new Set<VfsPath>();
    for (const path of paths) set.add(path);
    grants.set(instanceKey, set);
  }

  function callerFor(ctx: AppInstanceContext): VfsCaller {
    const user = tree.currentUser();
    const roots: VfsPath[] = [
      ...(fileManagers.has(ctx.appId) ? [userRoot(user), ROOT_APPS] : []),
      ...(options.extraRoots?.(ctx.appId) ?? []),
    ];
    return { appId: ctx.appId, user, roots, grants: grants.get(ctx.instanceKey) ?? [] };
  }

  /** Confine, or throw the §4.1 error. Returns the normalised path the tree should be given. */
  function resolve(ctx: AppInstanceContext, path: VfsPath, access: 'read' | 'write'): VfsPath {
    const result = confine(callerFor(ctx), path, access);
    if (result.ok) return result.path;

    // A refusal and a malformed path are different failures and must not answer alike: an app
    // needs to tell "you may not" from "that is not a path".
    if (result.error === 'FORBIDDEN' || result.error === 'READ_ONLY') {
      throw new HangarApiError(
        'FORBIDDEN',
        `${JSON.stringify(path)} is outside what ${ctx.appId} may reach`,
        { path, reason: result.error },
      );
    }
    throw new HangarApiError('BAD_REQUEST', `${result.error} for ${JSON.stringify(path)}`, {
      path,
      reason: result.error,
    });
  }

  /** Grammar errors have to keep their own code — `BAD_REQUEST`, not `FORBIDDEN`. */
  function guard<T>(path: string, work: () => Promise<T>): Promise<T> {
    return work().catch((error: unknown) => {
      throw toApiError(error, path, options.onQuotaExceeded);
    });
  }

  /**
   * Run the shell's picker and turn whatever comes back into grants. The picker browses with the
   * *shell's* reach; the app receives only what the user actually chose (§9.5).
   */
  async function runPicker(
    ctx: AppInstanceContext,
    mode: 'open' | 'save',
    opts: PickOpts | undefined,
  ): Promise<VfsPath[] | null> {
    if (!options.pickFiles) {
      throw new HangarApiError('UNSUPPORTED', 'no shell picker is attached');
    }
    const start = opts?.startIn ?? joinVfs(userRoot(tree.currentUser()), 'Documents');
    const startIn = normalizeVfsPath(start);
    const picked = await options.pickFiles(
      {
        mode,
        title: opts?.title ?? (mode === 'save' ? 'Save as' : 'Open'),
        startIn: startIn.ok ? startIn.path : userRoot(tree.currentUser()),
        ...(opts?.suggestedName !== undefined ? { suggestedName: opts.suggestedName } : {}),
        extensions: (opts?.extensions ?? []).map((ext) => ext.toLowerCase()),
        multiple: opts?.multiple ?? false,
        directory: opts?.directory ?? false,
      },
      ctx.appId,
    );
    if (picked === null) return null;

    // A *save* target may not exist yet, so the grant covers the name the user typed — and
    // only that name, never the directory it sits in.
    const granted = picked
      .map((path) => normalizeVfsPath(path))
      .filter((parsed): parsed is { ok: true; path: VfsPath; segments: string[] } => parsed.ok)
      .map((parsed) => parsed.path);
    if (granted.length === 0) return null;
    addGrants(ctx.instanceKey, granted);
    return granted;
  }

  return {
    callerFor,

    canReach: (ctx, path, access) => confine(callerFor(ctx), path, access).ok,

    grant: addGrants,

    revoke(instanceKey) {
      grants.delete(instanceKey);
    },

    grantsFor: (instanceKey) => [...(grants.get(instanceKey) ?? [])],

    list(ctx, dir) {
      return guard(dir, async () => tree.list(resolve(ctx, dir, 'read')));
    },

    read(ctx, path) {
      return guard(path, async () => tree.read(resolve(ctx, path, 'read')));
    },

    readText(ctx, path) {
      return guard(path, async () => tree.readText(resolve(ctx, path, 'read')));
    },

    write(ctx, path, data) {
      return guard(path, async () => tree.write(resolve(ctx, path, 'write'), data));
    },

    mkdir(ctx, path) {
      return guard(path, async () => tree.mkdir(resolve(ctx, path, 'write')));
    },

    move(ctx, from, to) {
      // Both ends are checked: moving *out of* a place is a write to that place.
      return guard(from, async () =>
        tree.move(resolve(ctx, from, 'write'), resolve(ctx, to, 'write')),
      );
    },

    remove(ctx, path) {
      return guard(path, async () => {
        await trash.trash(resolve(ctx, path, 'write'));
      });
    },

    versions(ctx, path) {
      return guard(path, async () => versions.list(resolve(ctx, path, 'read')));
    },

    restore(ctx, path, versionId) {
      // Restoring writes, so it needs write access even though it reads the history.
      return guard(path, async () => versions.restore(resolve(ctx, path, 'write'), versionId));
    },

    async pickOpen(ctx, opts) {
      const picked = await runPicker(ctx, 'open', opts);
      // A cancelled *open* is an empty result, not an error: `pickOpen` returns a list, and an
      // empty list says exactly what happened.
      if (picked === null) return [];
      const entries: FileEntry[] = [];
      for (const path of picked) {
        const found = await tree.stat(path);
        if (found) entries.push(found);
      }
      return entries;
    },

    async pickSave(ctx, opts) {
      const picked = await runPicker(ctx, 'save', opts);
      // `pickSave` returns one entry and has nothing empty to say, so a dismissal is an error
      // the app can recognise rather than a value it has to guess about (D-072).
      if (picked === null || picked[0] == null) {
        throw new HangarApiError('CANCELLED', 'the save picker was dismissed');
      }
      const target = picked[0];
      const existing = await tree.stat(target);
      // A save picker names a file that need not exist yet; the entry describes where it will go.
      return (
        existing ?? {
          path: target,
          name: basename(target),
          isDir: false,
          size: 0,
          mtime: 0,
          rev: 0,
        }
      );
    },

    watch(ctx, dir, cb) {
      return guard(dir, async () => {
        const root = resolve(ctx, dir, 'read');
        // A watcher must not leak events from outside the caller's reach — a grant can be
        // narrower than the directory it sits in.
        const caller = callerFor(ctx);
        return tree.watch(root, (event) => {
          if (confine(caller, event.path, 'read').ok) cb(event);
        });
      });
    },
  };
}
